import React, { useState, useEffect, useRef } from 'react';
import classes from './Reviews.module.css';
import Modal from "../../components/UI/Modal";
import noUserImage from "../../public/fakeUser/noUser.webp";

const Review = ({ review, isModal = false }) => {
    const [isOverflowing, setIsOverflowing] = useState(false);
    const reviewRef = useRef(null);

    useEffect(() => {
        if (reviewRef.current) {
            const { scrollHeight, clientHeight } = reviewRef.current;
            if (scrollHeight > clientHeight) {
                setIsOverflowing(true);
            }
        }
    }, []);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 7) {
            return `vor ${diffDays} Tagen`;
        } else if (diffDays < 30) {
            const weeks = Math.floor(diffDays / 7);
            return `vor ${weeks} ${weeks === 1 ? 'Woche' : 'Wochen'}`;
        } else {
            const months = Math.floor(diffDays / 30);
            return `vor ${months} ${months === 1 ? 'Monat' : 'Monaten'}`;
        }
    };

    const renderStars = (rating) => {
        const starArray = [];
        for (let i = 0; i < rating; i++) {
            starArray.push(
                <svg
                    key={i}
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="9"
                    viewBox="0 0 10 9"
                    fill="none"
                    className={classes.redStar}
                >
                    <g clipPath="url(#clip0_3947_4431)">
                        <path
                            d="M7.76885 6.70095L8.15793 8.9889L5.22363 7.44914L7.60133 5.73047L7.71482 6.38649L7.76885 6.70095Z"
                            fill="#DF315A"
                        />
                        <path
                            d="M2.73343 6.38783L2.84691 5.7318L0.474609 3.42758L3.75476 2.95048L5.22461 -0.00976562L6.69446 2.95048L9.97461 3.42758L2.29031 8.99023L2.73343 6.38783Z"
                            fill="#DF315A"
                        />
                    </g>
                    <defs>
                        <clipPath id="clip0_3947_4431">
                            <rect
                                width="9.5"
                                height="9"
                                fill="white"
                                transform="translate(0.474609 -0.00976562)"
                            />
                        </clipPath>
                    </defs>
                </svg>
            );
        }
        return starArray;
    };

    return (
        <div className={`${classes.review} ${isModal ? classes.reviewModal : ''}`}>
            <div className={classes.reviewerProfile}>
                <div className={classes.photoContainer}>
                    <img src={review?.reviewer?.profilePicture || noUserImage} alt={`${review?.reviewer?.firstName}'s review`} />
                </div>
                <div className={classes.headerReview}>
                    <span className="semiboldMiddle">{review?.reviewer?.firstName}</span>
                    <div className={classes.headerReviewInfos}>
                        <div>{renderStars(review?.rating)}</div>
                        <span className="semiboldXs">{formatDate(review?.createdAt)}</span>
                    </div>
                </div>
            </div>
            <div className={classes.reviewTextContainer}>
                <p
                    ref={reviewRef}
                    className={`${classes.text} ${isModal ? "regularLarge" : ''}`}
                >
                    {review?.content}
                </p>
                {isOverflowing && (
                    <button className={classes.showMoreButton}>
                        Mehr anzeigen
                    </button>
                )}
            </div>
        </div>
    );
};

export const Reviews = ({ reviews: data }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [reviews, setReviews] = useState([]);
    
    useEffect(() => {
        if (data) {
            setReviews(data);
        }
    }, [data]);

    const calculateAverageRating = () => {
        if (!reviews.length) return "-";
        const total = reviews.reduce((sum, review) => sum + review.rating, 0);
        return (total / reviews.length).toFixed(1);
    };

    return (
        reviews?.length > 0 && (
        <div className={classes.container}>
            <header className={classes.header}>
                <div className={`${classes.headerInfos} regularXl`}>
                    <h1 className={`${classes.title} semiboldMiddle`}>Bewertungen</h1>
                    <svg
                        className={classes.smallCircle}
                        xmlns="http://www.w3.org/2000/svg"
                        width="3"
                        height="4"
                        viewBox="0 0 3 4"
                        fill="none"
                    >
                        <circle cx="1.5" cy="1.69336" r="1.5" fill="#1F1F1F" />
                    </svg>
                    {reviews?.length > 0 &&
                        <div className={classes.rating}>
                            <span className={classes.ratingNumber}>{calculateAverageRating()}</span>
                            <svg
                                className={classes.star}
                                xmlns="http://www.w3.org/2000/svg"
                                width="19"
                                height="18"
                                viewBox="0 0 19 18"
                                fill="none"
                            >
                                <g clipPath="url(#clip0_3947_4570)">
                                    <path
                                        d="M14.2391 13.1393L14.9787 17.4738L9.40137 14.5568L13.9207 11.3008L14.1364 12.5436L14.2391 13.1393Z"
                                        fill="#1F1F1F"
                                    />
                                    <path
                                        d="M4.66741 12.5439L4.88311 11.3011L0.374023 6.93581L6.60868 6.03194L9.40246 0.423828L12.1962 6.03194L18.4309 6.93581L3.82517 17.4741L4.66741 12.5439Z"
                                        fill="#1F1F1F"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_3947_4570">
                                        <rect
                                            width="18.0569"
                                            height="17.0503"
                                            fill="white"
                                            transform="translate(0.374023 0.423828)"
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <span className={classes.reviews}>({reviews?.length} Bewertungen)</span>
                        </div>
                    }
                </div>
            </header>
            <div className={classes.reviewsContainer}>
                {reviews?.slice(0, 3).map(review => (
                    <Review
                        key={review?.id}
                        review={review}
                    />
                ))}
            </div>
            <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <div className={classes.modalContent}>
                    <header className={classes.modalHeader}>
                        <div className={classes.headerBewertungen}>
                            <h2 className='titleSemibold3'>Bewertungen</h2>
                            <svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4" fill="none">
                                <circle cx="1.87256" cy="2" r="1.5" fill="#1F1F1F"/>
                            </svg>
                            <span className='regularXl'>{calculateAverageRating()}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
                                <g clipPath="url(#clip0_11458_71949)">
                                <path d="M14.6117 13.2546L15.3512 17.589L9.77393 14.672L14.2933 11.416L14.509 12.6588L14.6117 13.2546Z" fill="#1F1F1F"/>
                                <path d="M5.03997 12.6592L5.25567 11.4163L0.746582 7.05104L6.98124 6.14717L9.77502 0.539062L12.5688 6.14717L18.8035 7.05104L4.19773 17.5894L5.03997 12.6592Z" fill="#1F1F1F"/>
                                </g>
                                <defs>
                                <clipPath id="clip0_11458_71949">
                                <rect width="18.0569" height="17.0503" fill="white" transform="translate(0.746582 0.539062)"/>
                                </clipPath>
                                </defs>
                            </svg>
                            <span className='regularXl'>({reviews.length} Bewertungen)</span>
                        </div>
                        <button className={classes.closeButton} onClick={() => setIsModalOpen(false)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.54023 9L0.0307617 1.49054L1.0213 0.5L8.53076 8.00947L16.0402 0.5L17.0308 1.49054L9.5213 9L17.0308 16.5095L16.0402 17.5L8.53076 9.99054L1.0213 17.5L0.0307617 16.5095L7.54023 9Z" fill="#1F1F1F"/>
                            </svg>
                        </button>
                    </header>
                    <div className={classes.reviewsModalContainer}>
                        {reviews?.map(review => (
                            <Review 
                                key={review?.id}
                                review={review}
                                isModal={true}
                            />
                        ))}
                    </div>
                </div>
            </Modal>
            {reviews.length > 3 && (
                <button className={`${classes.button} semiboldMiddle`} onClick={() => setIsModalOpen(true)}>
                    Alle {reviews.length} Bewertungen lesen
                </button>
            )}
        </div>
        )
    );
};

export default Reviews;